import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { SessionProvider } from './contexts/SessionContext';
import { ThemeProvider } from './contexts/ThemeContext';
import PrivateRoute from './components/PrivateRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import SubscriptionSelection from './pages/SubscriptionSelection';
import Packages from './pages/Packages';
import Checkout from './pages/Checkout';
import Start from './pages/Start';
import PackageShell from './pages/PackageShell';
import Dashboard from './pages/Dashboard';
import MasterYourMissesSession from './pages/MasterYourMissesSession';
import InitialAssessment from './pages/InitialAssessment';
import TakeAssessment from './pages/TakeAssessment';
import AssessmentResults from './pages/AssessmentResults';
import Leaderboard from './pages/Leaderboard';
import MicroLearning from './pages/MicroLearning';
import About from './pages/About';
import Landing from './pages/Landing';
import LandingV2 from './pages/LandingV2';
import LandingV3 from './pages/LandingV3';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Contact from './pages/Contact';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Testimonials from './pages/Testimonials';
import Careers from './pages/Careers';
import CareerRole from './pages/CareerRole';
import FirstStudents from './pages/FirstStudents';
import LandingRedesign from './pages/LandingRedesign';
import FinancialLiteracy from './pages/FinancialLiteracy';
import Profile from './pages/Profile';
import Inbox from './pages/Inbox';
import AptitudeDrill from './pages/AptitudeDrill';
import DailyFocus from './pages/DailyFocus';
import WeeklyMock from './pages/WeeklyMock';
import LiveExam from './pages/LiveExam';
import PracticeHistory from './pages/PracticeHistory';
import PracticeReview from './pages/PracticeReview';
import AptitudeHistory from './pages/AptitudeHistory';
import AptitudePath from './pages/AptitudePath';
import AptitudeReview from './pages/AptitudeReview';
import Banned from './pages/Banned';
import DiagnosticOnboarding from './pages/DiagnosticOnboarding';
import TelegramAskLomi from './pages/TelegramAskLomi';
import TelegramLesson from './pages/TelegramLesson';
import TelegramExams from './pages/TelegramExams';
import TelegramAptitudeHistory from './pages/TelegramAptitudeHistory';
import TelegramAptitudeReview from './pages/TelegramAptitudeReview';
import TelegramPracticeHistory from './pages/TelegramPracticeHistory';
import TelegramPracticeReview from './pages/TelegramPracticeReview';
import TelegramAddSchool from './pages/TelegramAddSchool';
import TelegramTestimonials from './pages/TelegramTestimonials';
import TelegramCheckout from './pages/TelegramCheckout';
import PublicQuestion from './pages/PublicQuestion';
import SlugLanding from './pages/SlugLanding';
// PaymentFlow import removed — /subscription redirects to /dashboard
// while the free-trial launch window is on. Restore when re-enabling.
import { PageViewBeacon } from './components/PageViewBeacon';
import FeedbackButton from './components/feedback/FeedbackButton';
import './App.css';

// Show the feedback button only on "destination" pages where the student
// is pausing between actions — not during focused flows (taking an
// assessment, a lesson, onboarding) where it competes with the primary CTA
// or the Ask Lomi entry points.
const FEEDBACK_PAGES = ['/dashboard', '/assessment-results', '/profile', '/subscription', '/leaderboard'];
const FloatingFeedback: React.FC = () => {
  const { pathname } = useLocation();
  // Public share pages should feel like a preview, not a product — hide the button.
  if (pathname.startsWith('/q/')) return null;
  const isDestination = FEEDBACK_PAGES.some(
    p => pathname === p || pathname.startsWith(p + '/'),
  );
  if (!isDestination) return null;
  return <FeedbackButton />;
};

/**
 * The root path. An authenticated student goes to THEIR home, which is the
 * ESSLCE dashboard unless they hold a package that declares its own — the
 * decision is made server-side and arrives on the profile, so this route and
 * login cannot disagree.
 *
 * `student.home` is already loaded by AuthContext, so there is no extra fetch
 * and no flash: while the profile is in flight `isLoading` holds, and after it
 * the destination is known.
 */
const HomeRoute: React.FC = () => {
  const { isAuthenticated, isLoading, student } = useAuth();
  if (isLoading) return null;
  if (!isAuthenticated) return <LandingV2 />;
  return <Navigate to={student?.home || '/dashboard'} replace />;
};

function App() {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
        <SubscriptionProvider>
          <SessionProvider>
            <div className="App">
            <PageViewBeacon />
            <Routes>
            <Route path="/r/:slug" element={<SlugLanding />} />
            <Route path="/first-students" element={<FirstStudents />} />
            <Route path="/first-students/:fc" element={<FirstStudents />} />
            <Route path="/founding-class" element={<FirstStudents />} />
            <Route path="/login" element={<Login />} />
            <Route path="/about" element={<About />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/testimonials" element={<Testimonials />} />
            {/* Public job board. Unauthed on purpose — most applicants have no
                Lomi account, and hiring is a brand surface as much as an intake. */}
            <Route path="/careers" element={<Careers />} />
            <Route path="/careers/:slug" element={<CareerRole />} />
            <Route path="/jobs" element={<Navigate to="/careers" replace />} />
            {/* /impact retired — the stories are the story. Numbers folded into /testimonials. */}
            <Route path="/impact" element={<Navigate to="/testimonials" replace />} />
            <Route path="/redesign" element={<LandingRedesign />} />
            <Route
              path="/register"
              element={
                <PrivateRoute>
                  <Register />
                </PrivateRoute>
              }
            />
            {/* The package-first entry path, built beside /register rather
                than replacing it. Nothing links here yet — slice 6 flips
                /register's redirect over in one edit. */}
            <Route
              path="/start"
              element={
                <PrivateRoute>
                  <Start />
                </PrivateRoute>
              }
            />
            <Route
              path="/subscription-selection"
              element={
                <PrivateRoute>
                  <SubscriptionSelection />
                </PrivateRoute>
              }
            />
            <Route
              path="/diagnostic-onboarding"
              element={
                <PrivateRoute>
                  <DiagnosticOnboarding />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            {/* Package catalog. No nav link yet on purpose — the catalog is
                behind package_catalog.mode and reachable by URL only until
                the picker is wired up. */}
            <Route
              path="/packages"
              element={
                <PrivateRoute>
                  <Packages />
                </PrivateRoute>
              }
            />
            {/* A real URL, not a panel — the bot deep-links here, since
                Telebirr's H5 flow can't run inside a Telegram chat. */}
            {/* Delivery shells — three layouts over the same lesson payload,
                so they can be compared on identical content. Preview-gated by
                the same catalog switch as everything else. */}
            <Route
              path="/p/:pkg/:shell"
              element={
                <PrivateRoute>
                  <PackageShell />
                </PrivateRoute>
              }
            />
            <Route
              path="/p/:pkg/:shell/:slug"
              element={
                <PrivateRoute>
                  <PackageShell />
                </PrivateRoute>
              }
            />
            <Route
              path="/packages/:cardKey/checkout"
              element={
                <PrivateRoute>
                  <Checkout />
                </PrivateRoute>
              }
            />
            <Route
              path="/review/misses/:subject"
              element={
                <PrivateRoute>
                  <MasterYourMissesSession />
                </PrivateRoute>
              }
            />
            <Route
              path="/initial-assessment"
              element={
                <PrivateRoute>
                  <InitialAssessment />
                </PrivateRoute>
              }
            />
            <Route
              path="/take-assessment"
              element={
                <PrivateRoute>
                  <TakeAssessment />
                </PrivateRoute>
              }
            />
            <Route
              path="/assessment-results"
              element={
                <PrivateRoute>
                  <AssessmentResults />
                </PrivateRoute>
              }
            />
            <Route
              path="/leaderboard"
              element={
                <PrivateRoute>
                  <Leaderboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/micro-learning"
              element={
                <PrivateRoute>
                  <MicroLearning />
                </PrivateRoute>
              }
            />
            {/* Financial Literacy pilot — unlinked, invite-only vertical.
                Isolated on its own route + access key so it never touches the
                ESSLCE student flow. Remove with the seed script's DELETE block. */}
            <Route
              path="/finance"
              element={
                <PrivateRoute>
                  <FinancialLiteracy />
                </PrivateRoute>
              }
            />
            {/* Subscription/PaymentFlow disabled during the free-trial
                launch window — the page renders an empty Duration /
                Additional Students form on mobile and blocks Continue.
                Bookmarks and any stale links bounce to the dashboard. */}
            <Route
              path="/subscription"
              element={<Navigate to="/dashboard" replace />}
            />
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              }
            />
            <Route
              path="/inbox"
              element={
                <PrivateRoute>
                  <Inbox />
                </PrivateRoute>
              }
            />
            <Route
              path="/aptitude-drill/today"
              element={
                <PrivateRoute>
                  <AptitudeDrill />
                </PrivateRoute>
              }
            />
            <Route
              path="/daily-focus/today"
              element={
                <PrivateRoute>
                  <DailyFocus />
                </PrivateRoute>
              }
            />
            <Route
              path="/weekly-mock/today"
              element={
                <PrivateRoute>
                  <WeeklyMock />
                </PrivateRoute>
              }
            />
            <Route
              path="/live"
              element={
                <PrivateRoute>
                  <LiveExam />
                </PrivateRoute>
              }
            />
            <Route
              path="/practice-history"
              element={
                <PrivateRoute>
                  <PracticeHistory />
                </PrivateRoute>
              }
            />
            <Route
              path="/practice-history/:sessionId"
              element={
                <PrivateRoute>
                  <PracticeReview />
                </PrivateRoute>
              }
            />
            <Route
              path="/aptitude-path"
              element={
                <PrivateRoute>
                  <AptitudePath />
                </PrivateRoute>
              }
            />
            <Route
              path="/aptitude-drill/history"
              element={
                <PrivateRoute>
                  <AptitudeHistory />
                </PrivateRoute>
              }
            />
            <Route
              path="/aptitude-drill/review/:sessionId"
              element={
                <PrivateRoute>
                  <AptitudeReview />
                </PrivateRoute>
              }
            />
            {/* Banned account landing — public so suspended users can read it. */}
            <Route path="/banned" element={<Banned />} />
            {/* Public shared-question landing — no auth */}
            <Route path="/q/:questionId" element={<PublicQuestion />} />

            {/* Telegram Mini App routes — auth handled internally via initData */}
            <Route path="/tg/ask-lomi" element={<TelegramAskLomi />} />
            <Route path="/tg/lesson/:moduleId" element={<TelegramLesson />} />
            <Route path="/tg/exams" element={<TelegramExams />} />
            <Route path="/tg/aptitude-drill/history" element={<TelegramAptitudeHistory />} />
            <Route path="/tg/aptitude-drill/review/:sessionId" element={<TelegramAptitudeReview />} />
            <Route path="/tg/practice-history" element={<TelegramPracticeHistory />} />
            <Route path="/tg/practice-history/:sessionId" element={<TelegramPracticeReview />} />
            <Route path="/tg/profile/add-school" element={<TelegramAddSchool />} />
            <Route path="/tg/testimonials" element={<TelegramTestimonials />} />
            {/* Paying for a package from inside Telegram. The bot serves
                lessons and questions in the chat; this is the one screen that
                needs a webview, because a receipt needs a file picker. */}
            <Route path="/tg/p/:cardKey/checkout" element={<TelegramCheckout />} />

            <Route path="/" element={<HomeRoute />} />
            <Route path="/landing-old" element={<Landing />} />
            <Route path="/landing-v3" element={<LandingV3 />} />
            </Routes>
            <FloatingFeedback />
          </div>
        </SessionProvider>
        </SubscriptionProvider>
      </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
